import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DetectMethodChanges, getShortUUID } from '../../utils';
import { ApgCopilotSession } from './apg-copilot-session';
import { ApgRollPositionsPanelComponent } from './apg-roll-positions-panel.component';
import {
    RollPositionsInputs,
    RollSuggestion,
    RollSuggestionContent,
    RollTabState,
    Run,
    RunOutcome,
    TabKind,
    Weekday
} from './apg-copilot-domain';
import { IApgCopilotFacade } from './apg-copilot-facade';
import { dayKey, isValidDate, weekdayOverrideKeys } from './apg-copilot-questionnaire';
import { OrionSettingsService, OrionSettingsUpdatedTopic } from '../../orion-settings-dialog/orion-settings.service';
import { MessageBusService } from '../../message-bus.service';
import { landmineWindowSpan } from './apg-roll-window';
import { ApgOrionRunRequest, IRollPositionsSkill, ROLL_POSITIONS_SKILL } from './apg-roll-positions-skill';
import { describeApgOrionFailure } from './backend-apg-orion-skill';
import { cloneRollInputs, diffRollInputs, hasAnyChange, ParamChangeMap } from './apg-copilot-param-diff';

// The APG CoPilot orchestrator — the content inside the host's dx-popup window. Owns the session,
// the active tab, the run flow, and the advisory Preview/Review facade calls. ADVISORY ONLY: the
// only host mutations it ever makes are setPreviewCell/clearPreviewCell (a highlight) and openReview
// (opens the existing dialog).
@Component({
    selector: 'ets-apg-copilot',
    templateUrl: './apg-copilot.component.html',
    styleUrls: ['./apg-copilot.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgCopilotComponent implements OnInit, AfterViewInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _zone: NgZone,
        @Inject(ROLL_POSITIONS_SKILL) private readonly _skill: IRollPositionsSkill,
        // CR-20260904-001 follow-on (2026-09-08): the shell-level ORION caps (top N, range width).
        private readonly _orionSettings: OrionSettingsService,
        private readonly _messageBus: MessageBusService
    ) {}

    private _session = new ApgCopilotSession(getShortUUID, () => Date.now());

    @Input() root: IApgCopilotFacade | null = null;

    @Output() previewActiveChange = new EventEmitter<boolean>();
    @Output() closeRequested = new EventEmitter<void>();

    // Grid-drift cue pushed to the host so the collapsed FAB carries the yellow while the window is shut
    // (OPG/HedgeMatrix parity). Emitted only on a real hot<->cold transition (deduped by _isStaleCached).
    @Output() staleChange = new EventEmitter<boolean>();

    // ---- dynamic popup height (owner 2026-07-07 "make dynamic height, just as in HG Copilot") ----
    // Duplicated from hg-copilot.component.ts: one ResizeObserver on the scrollable content column
    // emits the natural content height; the host (AdjustmentPricingGridComponent) owns the clamp + the
    // popup geometry. Covers every case: questionnaire collapse-to-breadcrumbs, results arriving after a
    // Run, tab switch (Roll positions <-> Zone), and the popup open animation.
    @Output() readonly contentHeightChange = new EventEmitter<number>();

    private _scrollContentEl: HTMLElement | null = null;
    private _contentResizeObserver: ResizeObserver | null = null;
    private _contentHeightRafId: number | null = null;
    private _lastEmittedContentHeight = -1;

    // Re-attaches the observer whenever the scroll-content element appears/disappears (it lives under
    // *ngIf="contentVisible", which toggles on portfolio switch).
    @ViewChild('scrollContent')
    set scrollContentRef(ref: ElementRef<HTMLElement> | undefined) {
        const el = ref ? ref.nativeElement : null;
        if (el === this._scrollContentEl) {
            return;
        }
        if (this._contentResizeObserver && this._scrollContentEl) {
            this._contentResizeObserver.unobserve(this._scrollContentEl);
        }
        this._scrollContentEl = el;
        if (el && this._contentResizeObserver) {
            this._contentResizeObserver.observe(el);
            this._scheduleContentMeasure();
        }
    }

    // Toggled off/on to recreate the questionnaire on portfolio switch (fresh empty inputs).
    contentVisible = true;

    activeTab: TabKind = 'rollPositions';
    collapsed = false;                     // questionnaire collapses to breadcrumbs after a Run
    rollInputs: RollPositionsInputs | null = null;
    rollValid = false;

    // Layer 2: chain-listed expirations ("yyyy-MM-dd") for the current underlying — the roll-date
    // candidate universe, fed to the exclusion calendar so only real expirations are toggleable AND to
    // the roll panel to restrict its date-boxes.
    availableExpirations: string[] = [];

    // The roll panel's Start ANCHOR reader (owner 2026-09-08, screencast 16-59-17: the earlier
    // ShortOption expiration, or today when that is past). ONE stable arrow bound once: a fresh
    // closure per change-detection pass would be a new @Input value every pass. Delegates to the
    // facade so the short-leg lookup and the QG-cursor / shell-clock choice live in the host.
    readonly anchorKeyReader: () => string | null = () => (this.root ? this.root.readRollAnchorKey() : null);

    // The anchor moves when the trader edits the shorts while ORION is open: the roll panel must
    // re-read it (an invalid Start rolls forward, an empty one seeds). Queried, not static: the panel
    // lives under *ngIf="contentVisible".
    @ViewChild(ApgRollPositionsPanelComponent)
    private _rollPanel: ApgRollPositionsPanelComponent | null = null;

    // True while readAvailableExpirations() is in flight -> the roll panel shows a loading indicator
    // and its date-boxes fall back to disabling weekends only until the chain resolves.
    expirationsLoading = false;

    // ---- stale-grid drift state (reversible snapshot compare) -------------------------------------
    // At Run we capture a VALUE fingerprint of the grid (positions asDto + settings template, incl.
    // Override-ATM). Every later position/settings EDIT re-compares live vs captured: hot when they
    // differ, COLD again the moment the grid is restored to the captured state. Reversible by construction
    // (a pure function of current state), unlike the old one-way "touched" latch. A template load/reload
    // or portfolio switch is a context change, NOT drift — it hard-resets the whole CoPilot.
    private _staleArmed = false;
    private _capturedFingerprint: string | null = null;
    private _isStaleCached = false;             // GRID-drift only; isStale unions it with the param diff
    private _hardResetScheduled = false;        // coalesces rapid double-triggers (portfolio + its template load)
    private _gridChangeSubs: Subscription[] = [];

    // ---- parameter-change-vs-run cues (CR-20260831-004) -------------------------------------------
    // The questionnaire fields that no longer match what the CURRENT run sent (apg-copilot-param-diff
    // key grammar). Empty while no run exists; re-emptied by a new Run or a hard reset. A FRESH object
    // per recompute — the OnPush questionnaire/breadcrumb children wake on the reference.
    paramChanges: ParamChangeMap = {};

    // The staleChange output is a UNION (grid drift OR changed params) since this CR — deduped here,
    // because the two sources recompute on different triggers.
    private _lastEmittedStale = false;

    ngOnInit(): void {
        this.ensureConversation();
        this._subscribeGridChanges();
        // A super user saving ORION Settings while this window is open: the roll panel's
        // [maxRangeDays] must re-bind from the new value, and this is an OnPush host, so the save
        // has to wake it explicitly.
        this._gridChangeSubs.push(
            this._messageBus.of<any>(OrionSettingsUpdatedTopic)
                .subscribe(() => this._changeDetector.markForCheck())
        );
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;   // older browsers: popup keeps its bound height, no auto-size (graceful degrade)
        }
        this._contentResizeObserver = new ResizeObserver(() => this._scheduleContentMeasure());
        if (this._scrollContentEl) {
            this._contentResizeObserver.observe(this._scrollContentEl);
            this._scheduleContentMeasure();
        }
    }

    ngOnDestroy(): void {
        this.teardown();
        this._gridChangeSubs.forEach(s => s.unsubscribe());
        this._gridChangeSubs = [];
        if (this._contentHeightRafId !== null) {
            cancelAnimationFrame(this._contentHeightRafId);
            this._contentHeightRafId = null;
        }
        if (this._contentResizeObserver) {
            this._contentResizeObserver.disconnect();
            this._contentResizeObserver = null;
        }
    }

    // Coalesce ResizeObserver bursts (popup open animation, collapse/expand transitions) into one measure
    // per frame. _measureAndEmitContentHeight skips the hidden state (kept-mounted popup => offsetHeight 0)
    // and unchanged repeats.
    private _scheduleContentMeasure(): void {
        if (this._contentHeightRafId !== null) {
            return;
        }
        this._contentHeightRafId = requestAnimationFrame(() => {
            this._contentHeightRafId = null;
            this._measureAndEmitContentHeight();
        });
    }

    private _measureAndEmitContentHeight(): void {
        const el = this._scrollContentEl;
        if (!el) {
            return;
        }
        const h = el.offsetHeight;
        if (h <= 0 || h === this._lastEmittedContentHeight) {
            return;   // hidden (kept-mounted popup) or unchanged
        }
        this._lastEmittedContentHeight = h;
        this._zone.run(() => this.contentHeightChange.emit(h));
    }

    // Called by the host when the popup opens.
    open(): void {
        this.ensureConversation();
        this.refreshAvailableExpirations();
        this._changeDetector.markForCheck();
    }

    // Called by the host once the popup is fully SHOWN. OnPush + dx-popup can leave the view stale on
    // reopen — the session/UI state survives (collapsed, cards, inputs are intact, verified) but the DOM
    // still reflects the pre-close render, so the collapsed breadcrumbs "disappear". Force a synchronous
    // re-sync + height re-measure. (markForCheck in open() is too early: the popup content is not in the
    // CD tree when the host runs its post-open detectChanges.)
    refreshView(): void {
        this._changeDetector.detectChanges();
        this._scheduleContentMeasure();
    }

    // Called by the host on portfolio switch — a full CoPilot reset (the session is bound to one portfolio).
    onPortfolioChanged(): void {
        this._hardReset();
    }

    // Full CoPilot reset — drop the drift snapshot, reset the session + questionnaire to defaults on the
    // CURRENT portfolio (RETURN to Roll positions), and recreate the view. Shared by the reset button, a
    // portfolio switch, and a template load/reload (answer #2: those are context changes, not drift).
    // Coalesced: a portfolio switch also fires its own template load, so guard against a double recreate.
    // The recreate is DEFERRED (works popup-open OR closed): the reset button lives inside this content, and
    // the dx-popup overlay is not reached by markForCheck's ambient tick — so detectChanges, past the
    // current handler. false tears the content down; true rebuilds it fresh (resets the roll panel state).
    private _hardReset(): void {
        if (this._hardResetScheduled) {
            return;   // already resetting this tick — coalesce
        }
        this._hardResetScheduled = true;
        this._disarmStale();
        this._freshConversation();
        // O91 (2026-09-08): the reset is the ONLY release of the grid - lifts the glass and clears
        // the Override ATM ORION wrote. Reset button, portfolio switch and template load all funnel
        // here, so all three release. Closing the window does NOT come here, by design (O89).
        if (this.root) {
            this.root.releaseOrionHold();
        }
        Promise.resolve().then(() => {
            this._hardResetScheduled = false;
            this.contentVisible = false;
            this._changeDetector.detectChanges();
            this.contentVisible = true;
            this._changeDetector.detectChanges();
        });
    }

    // Reset the CoPilot session + questionnaire to defaults on the CURRENT portfolio: clear the advisory
    // preview, drop any in-flight run (a new session; the old run's late result is discarded by
    // completeRun's currency check), reset inputs, RETURN to the Roll positions tab. Does NOT toggle
    // contentVisible — _hardReset owns the view recreate. Touches ONLY the CoPilot window.
    private _freshConversation(): void {
        this.teardown();
        this._session = new ApgCopilotSession(getShortUUID, () => Date.now());
        this.activeTab = 'rollPositions';
        this.collapsed = false;
        this.rollInputs = null;
        this.rollValid = false;
        this.ensureConversation();
        this.refreshAvailableExpirations();
        // CR-20260831-004: refresh = full reset of the run baseline ("as if nothing has been
        // applied") — the new session has no run, so the diff empties and stays empty until the
        // next Run.
        this._recomputeParamChanges();
    }

    private ensureConversation(): void {
        if (!this._session.conversation) {
            const pfId = this.root ? this.root.readSelectedPortfolioId() : null;
            this._session.startConversation(pfId || '');
        }
    }

    // Fetch the underlying's listed expirations (async) for the exclusion calendar (Layer 2) + the
    // roll panel's date-box restriction. Drives expirationsLoading so the panel can show a loading
    // indicator + fall back to weekend-only blocking until the chain resolves.
    private refreshAvailableExpirations(): void {
        if (!this.root) {
            this.availableExpirations = [];
            this.expirationsLoading = false;
            return;
        }
        this.expirationsLoading = true;
        this._changeDetector.markForCheck();
        this.root.readAvailableExpirations()
            .then(dates => {
                this.availableExpirations = dates || [];
                this.expirationsLoading = false;
                this._changeDetector.markForCheck();
            })
            .catch(() => {
                this.availableExpirations = [];
                this.expirationsLoading = false;
                this._changeDetector.markForCheck();
            });
    }

    private teardown(): void {
        if (this.root) {
            this.root.clearPreviewCell();   // never leave a highlight behind
        }
        this.previewActiveChange.emit(false);
    }

    // ---- stale-grid drift wiring ------------------------------------------------------------------
    // Subscribe to the grid's EXISTING change detectors (via the facade). A position/settings EDIT
    // re-compares the live fingerprint against the one captured at Run (reversible hot/cold). A template
    // load/reload hard-resets the whole CoPilot (context change, not drift) — same as a portfolio switch.
    private _subscribeGridChanges(): void {
        if (!this.root) {
            return;
        }
        this._gridChangeSubs.push(
            this.root.observePositionsChanged().subscribe(() => {
                this._recomputeStale();
                // Owner 2026-09-08: shorts moved past the Start -> the Start is no longer legal.
                if (this._rollPanel) {
                    this._rollPanel.onAnchorChanged();
                }
            })
        );
        this._gridChangeSubs.push(
            this.root.observeSettingsChanged().subscribe(() => this._recomputeStale())
        );
        this._gridChangeSubs.push(
            this.root.observeTemplateReloaded().subscribe(() => this._hardReset())
        );
    }

    // Compare the live grid fingerprint against the one captured at Run. Fires only on edits (never per CD
    // tick), so the JSON serialization cost is bounded to actual user changes. Emits + repaints only on a
    // hot<->cold transition.
    private _recomputeStale(): void {
        let stale = false;
        if (this._staleArmed && this.root && this._capturedFingerprint !== null) {
            stale = this.root.readGridStateFingerprint() !== this._capturedFingerprint;
        }
        if (stale === this._isStaleCached) {
            return;
        }
        this._isStaleCached = stale;
        this._emitStaleUnion();
        this._changeDetector.markForCheck();
    }

    // CR-20260831-004: re-diff the LIVE questionnaire inputs against the current run's baseline.
    // Called from every questionnaire edit, from Run fire (both sides equal => empty) and from the
    // fresh conversation (baseline gone => empty).
    private _recomputeParamChanges(): void {
        const run = this.activeRun;
        this.paramChanges = diffRollInputs(run ? run.sentInputs : null, this.rollInputs);
        this._emitStaleUnion();
    }

    // One deduped push of the UNION to the host (the collapsed FAB): grid drift OR changed params —
    // "if any parameters are highlighted yellow, the collapsed button also must be yellow"
    // (owner ruling 2026-08-31).
    private _emitStaleUnion(): void {
        const stale = this._isStaleCached || hasAnyChange(this.paramChanges);
        if (stale === this._lastEmittedStale) {
            return;
        }
        this._lastEmittedStale = stale;
        this.staleChange.emit(stale);
    }

    // Post-run baseline (answer #5): snapshot the exact grid state the CoPilot priced against. Any later
    // edit that moves away from this snapshot goes hot; returning to it goes cold. A fresh run re-snapshots.
    private _armStaleBaseline(): void {
        this._staleArmed = true;
        this._capturedFingerprint = this.root ? this.root.readGridStateFingerprint() : null;
        this._recomputeStale();   // live == captured now => cold (clears any prior yellow)
    }

    private _disarmStale(): void {
        this._staleArmed = false;
        this._capturedFingerprint = null;
        this._recomputeStale();   // => cold
    }

    // Yellow iff the live grid differs from the captured snapshot — GRID DRIFT ONLY. The tab-bar
    // reset button reads this, and the CR-20260831-004 owner ruling (screencast, defect round 1)
    // is that the refresh/reset button reports portfolio/underlying drift only; changed parameters
    // carry their own per-control yellow and light the collapsed FAB (the staleChange union),
    // never this button. Cheap cached boolean — safe every CD pass.
    get isStale(): boolean {
        return this._isStaleCached;
    }

    // Whole-dialog reset (the tab-bar refresh button). Resets the CoPilot window AND releases the grid
    // (O91, 2026-09-08 - REPLACES the earlier "the underlying APG grid is never touched (answer #1)":
    // the release clears the Override ATM ORION wrote into the shared settings). Enabled during a run —
    // the fresh session drops it silently.
    onReset(): void {
        this._hardReset();
    }

    @DetectMethodChanges()
    onTabClick(tab: TabKind): void {
        this.activeTab = tab;
    }

    @DetectMethodChanges()
    onRollState(s: RollTabState): void {
        this.rollInputs = s.inputs;
        this.rollValid = s.valid;
        // CR-20260831-004: every questionnaire edit funnels through here — re-diff vs the run.
        this._recomputeParamChanges();
    }

    @DetectMethodChanges()
    onBreadcrumbEdit(): void {
        this.collapsed = false;   // re-expand the questionnaire to edit
    }

    get rollTab() {
        return this._session.tab('rollPositions');
    }

    get activeRun(): Run | null {
        const t = this.rollTab;
        return t ? t.currentRun : null;
    }

    get activeSuggestions(): RollSuggestion[] {
        const t = this.rollTab;
        return t ? t.suggestions : [];
    }

    get canRun(): boolean {
        return !!this.root
            && this.activeTab === 'rollPositions'
            && this.rollValid
            && !this._session.isRunInFlight
            && !!this.root.readSelectedPortfolioId();
    }

    get currentZone(): number | null {
        return this.root ? this.root.readCurrentZone() : null;
    }

    // CR-20260904-001 (BR-W1 / REQ-4): the widest range a trader may ask for, handed to the roll
    // panel. Since the 2026-09-08 ruling it is the shell-level "Max Query Range (days)" ORION
    // setting (default 21) and it binds EVERYONE - the super user included.
    get maxRangeDays(): number | null {
        return this._orionSettings.apgMaxQueryRangeDays;
    }

    // CR-20260915-002: the APG ORION's "Weekdays to exclude" - the STARTING weekday-exclusion set
    // of the date block (default Mon), handed to the roll panel. The service hands out the SAME
    // array instance until the next save, so this getter is a stable [input] binding; the
    // OrionSettingsUpdated subscription in ngOnInit wakes this OnPush host when a super user
    // saves, and the panel reads the new set at its next seeding moment (Single -> Range).
    get defaultWeekdayExclusions(): Weekday[] {
        return this._orionSettings.apgWeekdayExclusions;
    }

    /**
     * "Show top N results" — handed to the results list as its DISPLAY limit (ruling R-B,
     * 2026-09-10).
     *
     * ⚠ IT USED TO TRAVEL ON THE RUN REQUEST. It does not any more, and that is the ruling, not a
     * tidy-up: the delivery carries every window date because the Price Box feeds its columns from
     * it, so a cut anywhere before the render would blank the columns of the dates it dropped.
     * Read as a getter rather than captured at run time, so changing the setting re-renders the
     * cards already on screen without re-running.
     */
    get maxCards(): number | null {
        return this._orionSettings.apgShowTopN;
    }

    @DetectMethodChanges({ isAsync: true })
    async onRun(): Promise<void> {
        if (!this.canRun || !this.root || !this.rollInputs) {
            return;
        }

        // O88 (2026-09-08): Ask ORION is the COMMIT - ORION takes the grid before anything else.
        // The positions + settings columns go under the host's glass and Apply dies on every path,
        // because the ATM written next lands in the SHARED settings. Only _hardReset gives it back.
        this.root.takeOrionHold();

        // The ATM is a calc input, so it is fixed FIRST. Owner ruling 2026-09-08: no Override-ATM
        // pop-up on Ask ORION - the zone decides it silently (zone as custom ATM when a short is off
        // it, market ATM otherwise). The host persists the choice so it reaches the settings we send.
        this.root.applyOrionAtm();

        if (!this.root || !this.rollInputs) {
            return;
        }

        // Clear any prior preview + results; acquire the single-flight run.
        this.root.clearPreviewCell();
        this.previewActiveChange.emit(false);
        this._session.resetTabForReRun('rollPositions');
        // CR-20260831-004: sentInputs is the param-diff BASELINE now, so it gets its own deep copy
        // — the live reference must never alias the "snapshot of what was sent".
        const run = this._session.startRun('rollPositions', cloneRollInputs(this.rollInputs));
        if (!run) {
            return;   // single-flight: another run in flight
        }
        this.collapsed = true;   // collapse questionnaire to breadcrumbs
        this._recomputeParamChanges();   // live == sent at this instant => every param cue clears

        try {
            // Paint the pending state NOW (spinner + collapsed breadcrumbs) BEFORE the round trip.
            // @DetectMethodChanges({isAsync}) only runs CD after this whole method resolves, so
            // without this explicit flush the "Running ORION…" spinner would never show. Inside the
            // try so even a CD throw routes to completeRun('terminalFailed') and releases the
            // single-flight lock (otherwise the run would stick with the spinner spinning).
            this._changeDetector.detectChanges();

            // The expanded template settings, exactly as an Apply builds them, with the sequential
            // fan-out suppressed. `customDates` is left EMPTY on purpose: the backend resolves the
            // window from the dates and exclusions below (contract A1 §4.1), because Maven builds
            // this same array on the backend from a template id and has no browser to ask.
            const settings = await this.root.buildOrionPricingSettings();

            // WARM THE PRICE BOX'S COLOURS, and nothing more (leader ruling Q2, 2026-09-10).
            //
            // ⚠ THIS NO LONGER GATES THE RUN. The run's own calendar read happens on the backend,
            // where a failure fails loudly (contract A1 §4.3). What is left here is the colouring
            // fetch for the window the trader entered, so the columns are already coloured when the
            // delivery lands. A failure is therefore a COLOURING failure — and it is REPORTED, not
            // swallowed: silence is how "the calendar is down" turns into "these days look clear",
            // which is the one failure the landmine work exists to prevent.
            this.warmLandmineColours();

            // EXACTLY ONE DATE VARIANT PER KIND, the other two null — the shape the backend
            // validator refuses by name if it is broken. Branch on the discriminator rather than
            // reading all three off the union: `single` and `start`/`end` never coexist.
            const selection = this.rollInputs.date;
            const single = selection.kind === 'single' ? this.dayKeyOf(selection.single) : null;
            const rangeStart = selection.kind === 'range' ? this.dayKeyOf(selection.start) : null;
            const rangeEnd = selection.kind === 'range' ? this.dayKeyOf(selection.end) : null;

            const request: ApgOrionRunRequest = {
                panelInstanceId: this._session.panelInstanceId,
                // ⚠ THE COMPOSITE KEY, `userId@id`, NOT THE BARE ID. The wire field is named
                // `portfolioId`, but the backend resolves the zone by the composite — the
                // assignment lookup matches on it exactly, and a Type 2 zone's value is stored
                // under it. Sending the bare id made a portfolio that plainly has a zone come back
                // "no current zone", with both #2B and #3 priced, while the footer and the Zone tab
                // showed the zone on the same screen (they send the composite). Caught at G1.
                portfolioId: this.root.readSelectedPortfolioKey() as string,
                adjustmentPricingSettings: settings,
                dateKind: selection.kind,
                date: single,
                rangeStart: rangeStart,
                rangeEnd: rangeEnd,
                // Sent as the trader set them. BR-W2 ("a single date takes no exclusions") is a
                // RULE, and every rule now lives on the backend — pre-emptying them here would put
                // one of them back on this side.
                weekdayExclusions: this.rollInputs.weekdayExclusions.slice(),
                specificDateExclusions: this.dayKeysOf(this.rollInputs.specificDateExclusions),
                weekdayOverrides: weekdayOverrideKeys(this.rollInputs)
            };

            // ONE CALL. It sends RunApgOrionSkill, waits for ApgOrionCardsReadyDto on the event
            // stream and hands back the cards — the whole of what used to be about 1,150 lines of
            // browser reasoning over a GetAdjustmentSolutions round trip.
            const result = await this._skill.run(request);

            // THE PRICE BOX IS FED FROM THE DELIVERY (ruled 2026-09-10; leader rulings L37 / L50).
            // The run fires no pricing call of its own any more: the delivery carries EVERY window
            // adjustment, the grid populates its columns from it, and those columns price live from
            // their own strategy-code subscriptions. This is exactly why the delivery is uncapped —
            // a top-N cut on the wire would blank the columns of every date it dropped.
            //
            // ⚠ `priceBoxAdjustments`, NOT `contents`. The cards are only half the window: a Monday
            // or a report day never becomes a card, yet its column is on screen today with its
            // credit in it. The client hands over the union already flattened.
            //
            // ⚠ AND IT RUNS ON AN `empty` OUTCOME TOO — a single report day yields zero cards and
            // its column must still be drawn. Nothing here is gated on cards existing.
            await this.root.populatePriceBoxFromOrion(result.priceBoxAdjustments);

            // Re-attach the LIVE money. The frozen figure came over the wire; the card's cost has to
            // tick off the SAME quote-driven getter the Price Box cell binds, or the header and the
            // reason line drift apart as quotes move (the demo defect: a +$32.43 header against a
            // $32.38 reason).
            this.attachLivePrices(result.contents);

            run.warnings = result.warnings;

            const outcome: RunOutcome = result.contents.length > 0 ? 'succeeded' : 'empty';
            this._session.completeRun(run.id, outcome, result.contents, null);
            this._armStaleBaseline();   // capture the grid state this run priced against (answer #5)
        } catch (e) {
            // The reason travels with the failure. A refusal names itself from the backend's closed
            // list, a supersede says so, and anything else is a terminal failure — never a bare
            // "failed", and never a silent empty.
            this._session.completeRun(run.id, 'terminalFailed', [], describeApgOrionFailure(e));
        }
    }

    // 'yyyy-MM-dd' from a questionnaire Date, or null. LOCAL calendar parts, never toISOString —
    // that converts to UTC and can shift a picked calendar date across midnight.
    private dayKeyOf(d: Date | null | undefined): string | null {
        return isValidDate(d) ? dayKey(d as Date) : null;
    }

    private dayKeysOf(dates: Date[] | null | undefined): string[] {
        const out: string[] = [];
        const list = dates || [];
        for (let ix = 0; ix < list.length; ix++) {
            if (isValidDate(list[ix])) {
                out.push(dayKey(list[ix]));
            }
        }
        return out;
    }

    /**
     * Fetch the landmine verdicts for the window the trader entered, so the Price Box columns are
     * coloured by the time the delivery lands. Fire-and-forget by design — the run does not wait on
     * it — but a failure is REPORTED, never swallowed (leader ruling Q2, 2026-09-10).
     */
    private warmLandmineColours(): void {
        if (!this.root || !this.rollInputs) {
            return;
        }
        const span = landmineWindowSpan(this.rollInputs);
        Promise.resolve(this.root.ensureLandmineDays(span)).catch(e => {
            const cause = (e && e.message) ? String(e.message) : '';
            console.error(
                '[orion] the economic calendar could not be read for this window' +
                (cause ? ' (' + cause + ')' : '') +
                '. The Price Box columns are NOT coloured for it, and an uncoloured column means ' +
                'UNKNOWN — never clear. The run itself is unaffected: the backend reads its own ' +
                'calendar and fails the run loudly if it cannot.');
        });
    }

    /**
     * Give every card the live price accessor of its own Price Box cell.
     *
     * ⚠ WHY HERE AND NOT IN THE CLIENT. The accessor is a closure over a grid cell, and the grid is
     * the host's. The client maps the wire; the host owns the live data. Called AFTER the Price Box
     * has populated from the same delivery, so every card has a cell to point at. A card whose cell
     * is missing keeps its frozen figure rather than showing nothing.
     */
    private attachLivePrices(contents: RollSuggestionContent[]): void {
        if (!this.root) {
            return;
        }
        for (let ix = 0; ix < contents.length; ix++) {
            const accessor = this.root.readLivePriceAccessor(contents[ix].previewKey);
            if (accessor) {
                contents[ix].livePrice = accessor;
            }
        }
    }

    @DetectMethodChanges({ isAsync: true })
    async onPreviewToggle(s: RollSuggestion): Promise<void> {
        if (!this.root) {
            return;
        }
        const nowPreviewed = !s.state.previewed;
        // One active preview at a time — clear every other card's previewed flag first.
        this.activeSuggestions.forEach(x => { x.state.previewed = false; });
        s.state.previewed = nowPreviewed;
        if (nowPreviewed) {
            this.root.setPreviewCell(s.content.previewKey);
        } else {
            this.root.clearPreviewCell();
        }
        this.previewActiveChange.emit(this.activeSuggestions.some(x => x.state.previewed));
    }

    onReview(s: RollSuggestion): void {
        if (!this.root) {
            return;
        }
        this.root.openReview(s.content.adjustments);
    }

    onClose(): void {
        this.closeRequested.emit();
    }
}
